/*
 * Audiences list-management API layer (BULK_EMAIL_MANAGEMENT_2026-10-02 §7, §12).
 *
 * HARD RULE: audience, person, candidate and merge ids are 19-digit integers
 * that lose precision as JavaScript numbers. Every response here is parsed
 * with json-bigint in storeAsString mode, so any integer above 2^53 arrives
 * as a STRING and stays a string end to end (keys, URLs, request bodies).
 * Never Number() an id; send it back as the string you received.
 *
 * Auth: ID token (the service layer's convention) + x-app-id. Routes marked
 * Administrator in the spec authorize on the ACCESS token's cognito:groups
 * (probed on the campaign review queue 2026-08-10), so `admin: true` sends
 * that token instead.
 *
 *   audienceCall(path, opts)   → BASE/v1/audience/...   (AudienceService)
 *   personCall(path, opts)     → BASE/v1/person/...     (AudienceService)
 *   suppressionCall(path,opts) → BASE/v1/campaign/email/suppression...  (CampaignService)
 */
import endpoints from "../../endpoints";
import account from "../../data/account";
import { AuthBase } from "../../../plunk/service/auth-base";
import { ensureFreshToken } from "../../endpoints/market-intelligence";
import __JSONbigFactory from "json-bigint";
const JSONbigString = __JSONbigFactory({
  storeAsString: true
});
const BASE = endpoints.audience && endpoints.audience.root || ""; // https://api.../v1

export function parseBig(text) {
  if (!text) return null;
  try {
    return JSONbigString.parse(text);
  } catch (e) {
    return null;
  }
}
export function qs(params) {
  const clean = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return clean.length ? `?${clean.join("&")}` : "";
}
async function authHeaders({
  admin = false,
  json = true
} = {}) {
  const tenantId = (account.get() || {}).tenantId || "";
  const h = {
    "x-app-id": tenantId,
    "x-account-id": tenantId
  };
  if (json) h["Content-Type"] = "application/json";
  let bearer;
  if (admin) bearer = AuthBase.accessToken || (await ensureFreshToken());else bearer = (await ensureFreshToken()) || AuthBase.accessToken;
  if (bearer) h.Authorization = "Bearer " + bearer;
  return h;
}
export class ApiError extends Error {
  constructor(status, data, text) {
    super(data && (data.error || data.message) || text || `HTTP ${status}`);
    this.status = status;
    this.data = data;
  }
}

/** Core call. opts: {method, body (object → JSON), form (FormData), query, admin, raw (return Response)} */
export async function call(url, {
  method = "GET",
  body,
  form,
  query,
  admin = false,
  raw = false
} = {}) {
  const headers = await authHeaders({
    admin,
    json: !form
  });
  const res = await fetch(url + qs(query), {
    method,
    headers,
    mode: "cors",
    body: form ? form : body !== undefined ? JSON.stringify(body) : undefined
  });
  if (raw) return res;
  const text = await res.text();
  const data = parseBig(text);
  if (!res.ok) throw new ApiError(res.status, data, text);
  return data === null ? text : data;
}
export const audienceCall = (path, opts) => call(`${BASE}/audience${path}`, opts);
export const personCall = (path, opts) => call(`${BASE}/person${path}`, opts);
export const suppressionCall = (path, opts) => call(`${BASE}/campaign/email/suppression${path || ""}`, opts);
// Workspace email settings (CampaignService). "Inactive after" lives in this JSON (inactivityDays) and is read by the
// nightly hygiene job; the Audiences → Health tab edits it from 2026-10-03 (moved off the Email → Senders card).
export const getEmailSettings = () => call(`${BASE}/campaign/email/settings`);
export const putInactivityDays = async days => {
  const cur = await call(`${BASE}/campaign/email/settings`);
  return call(`${BASE}/campaign/email/settings`, {
    method: "PUT",
    body: {
      ...(cur || {}),
      inactivityDays: Number(days) || 365
    },
    admin: true
  });
};

/** Download a CSV export (auth header → blob → anchor). */
export async function downloadCsv(url, fileName) {
  const res = await call(url, {
    raw: true
  });
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, parseBig(text), text);
  }
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

/** Endpoints built in parallel with this UI: a 404 / 405 / 501 means "not deployed yet" and resolves to null. */
export const softFail = p => p.catch(e => {
  if (e && (e.status === 404 || e.status === 405 || e.status === 501)) return null;
  throw e;
});
export const isSoft = e => !!e && (e.status === 404 || e.status === 405 || e.status === 501);
export function toast(msg) {
  const t = document.querySelector("paper-toast");
  if (t && msg) t.show(msg);
}
export function isAdministrator() {
  return account.inGroup("Administrator") || account.inGroup("root") || account.inGroup("administrator") || account.inGroup("owner");
}

// ── lists ──────────────────────────────────────────────────────────────────
export const listAudiences = (kind, q) => audienceCall("", {
  query: {
    kind,
    q
  }
});
export const createAudience = body => audienceCall("/create", {
  method: "POST",
  body
});
export const updateAudience = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}`, {
  method: "PUT",
  body
});
export const refreshAudience = id => audienceCall(`/${encodeURIComponent(String(id))}/refresh`, {
  method: "POST"
});
export const estimateRule = ruleJson => audienceCall("/rules/estimate", {
  method: "POST",
  body: {
    ruleJson
  }
});
// {count, withEmail, sample:[{name, email}]}; falls back to the count-only estimate where the preview is not deployed.
export const previewRule = ruleJson => audienceCall("/rules/preview", {
  method: "POST",
  body: {
    ruleJson
  }
}).catch(e => {
  if (isSoft(e)) return estimateRule(ruleJson);
  throw e;
});
export const getMembers = (id, q, top, skip) => audienceCall(`/${encodeURIComponent(String(id))}/members`, {
  query: {
    q,
    $top: top,
    $skip: skip
  }
});
export const addMembers = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}/members`, {
  method: "POST",
  body
});
export const removeMembers = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}/members`, {
  method: "DELETE",
  body
});
export const importMembers = (id, file, mode, applySuggestions) => {
  const form = new FormData();
  form.append("file", file, file.name);
  return audienceCall(`/${encodeURIComponent(String(id))}/members/import`, {
    method: "POST",
    form,
    query: {
      mode,
      applySuggestions: applySuggestions ? "true" : "false"
    }
  });
};
export const exportMembers = (id, name) => downloadCsv(`${BASE}/audience/${encodeURIComponent(String(id))}/export`, `${(name || "audience").replace(/[^\w\- ]+/g, "").trim().replace(/ /g, "-")}.csv`);
export const getLinks = id => audienceCall(`/${encodeURIComponent(String(id))}/links`);
export const upsertLink = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}/links`, {
  method: "POST",
  body
});
export const syncLinkNow = (id, rail) => audienceCall(`/${encodeURIComponent(String(id))}/links/${encodeURIComponent(rail)}/sync-now`, {
  method: "POST"
});
export const deleteLink = (id, rail) => audienceCall(`/${encodeURIComponent(String(id))}/links/${encodeURIComponent(rail)}`, {
  method: "DELETE"
});
export const getHealth = range => audienceCall("/health", {
  query: {
    range
  }
});
export const getListHealth = (id, range) => audienceCall(`/${encodeURIComponent(String(id))}/health`, {
  query: {
    range
  }
});
export const purgeInactive = body => audienceCall("/purge/inactive", {
  method: "POST",
  body
});
// ── list detail additions (2026-10-04; built in parallel, soft-fail on 404/405/501) ──
export const getSummary = (id, range) => softFail(audienceCall(`/${encodeURIComponent(String(id))}/summary`, {
  query: {
    range
  }
}));
export const bulkMembers = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}/members/bulk`, {
  method: "POST",
  body
});
export const membersFromList = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}/members/from-list`, {
  method: "POST",
  body
});
export const membersFromRule = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}/members/from-rule`, {
  method: "POST",
  body
});
export const snapshotAudience = (id, name) => audienceCall(`/${encodeURIComponent(String(id))}/snapshot`, {
  method: "POST",
  body: {
    name
  }
});
export const convertAudience = (id, body) => audienceCall(`/${encodeURIComponent(String(id))}/convert`, {
  method: "POST",
  body
});
export const overlapDetail = audienceIds => softFail(audienceCall("/overlap-detail", {
  method: "POST",
  body: {
    audienceIds: audienceIds.map(String)
  }
}));
export const fromDifference = body => audienceCall("/from-difference", {
  method: "POST",
  body
});
export const getListCards = () => softFail(audienceCall("/cards"));

// ── duplicates / merge / policy / erase / consent ──────────────────────────
export const getDuplicates = (status, minScore, top, skip, audienceId) => personCall("/duplicates", {
  query: {
    status,
    minScore,
    audienceId,
    $top: top,
    $skip: skip
  }
});
export const mergeAutoDuplicates = body => personCall("/duplicates/merge-auto", {
  method: "POST",
  body: body || {}
});
export const mergeCandidate = (id, body) => personCall(`/duplicates/${encodeURIComponent(String(id))}/merge`, {
  method: "POST",
  body: body || {}
});
export const dismissCandidate = id => personCall(`/duplicates/${encodeURIComponent(String(id))}/dismiss`, {
  method: "POST"
});
export const mergePersons = (survivorId, mergedId, strategy) => personCall("/merge", {
  method: "POST",
  body: {
    survivorId: String(survivorId),
    mergedId: String(mergedId),
    strategy
  }
});
export const getMerge = mergeId => personCall(`/merges/${encodeURIComponent(String(mergeId))}`);
export const undoMerge = mergeId => personCall(`/merges/${encodeURIComponent(String(mergeId))}/undo`, {
  method: "POST"
});
export const getDedupePolicy = () => personCall("/dedupe-policy");
export const putDedupePolicy = policy => personCall("/dedupe-policy", {
  method: "PUT",
  body: policy,
  admin: true
});
export const erasePerson = id => personCall(`/${encodeURIComponent(String(id))}/erase`, {
  method: "POST",
  admin: true
});
export const getConsent = id => personCall(`/${encodeURIComponent(String(id))}/consent`);

// ── suppression (CampaignService) ──────────────────────────────────────────
export const getSuppression = (reason, q, top, skip) => suppressionCall("", {
  query: {
    reason,
    q,
    $top: top,
    $skip: skip
  }
});
export const addSuppression = (emails, reason, note) => suppressionCall("", {
  method: "POST",
  body: {
    emails,
    reason,
    note
  }
});
export const removeSuppression = id => suppressionCall(`/${encodeURIComponent(String(id))}`, {
  method: "DELETE",
  admin: true
});
export const importSuppression = (file, reason, note, preview) => {
  const form = new FormData();
  form.append("file", file, file.name);
  if (reason) form.append("reason", reason);
  if (note) form.append("note", note);
  return suppressionCall("/import", {
    method: "POST",
    form,
    query: {
      reason,
      note,
      preview: preview ? "true" : undefined
    }
  });
};
export const getSuppressionStats = audienceId => softFail(suppressionCall("/stats", {
  query: {
    audienceId
  }
}));
export const purgeExpiredSuppression = () => suppressionCall("/purge-expired", {
  method: "POST",
  admin: true
});
export const exportSuppression = reason => downloadCsv(`${BASE}/campaign/email/suppression/export${qs({
  reason
})}`, `email-suppression${reason ? "-" + reason : ""}.csv`);

// ── person summary for merge preview cards (ContactService, read-only) ─────
// GET /v1/activity/contacts/{id} is the existing contact read (detail-v2 uses
// it through the 'activity' root); the merge preview only needs identity
// fields, so a failure degrades to the candidate's own name/email columns.
const ACTIVITY = endpoints.activity && endpoints.activity.root || "";
export const getPersonSummary = id => call(`${ACTIVITY}/contacts/${encodeURIComponent(String(id))}`);